import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BedDouble, Plane, Plus, Ticket, Train, X, Icon, type LucideIcon } from '../../../ui/Icon';
import { withinLimit } from '../../../../utils/uploadLimits';
import { ATTACHMENT_ACCEPT, stagedFileIcon } from '../../bookings/documentFiles';
import { MAX_TICKET_FILES_PER_KIND, TICKET_KINDS, type TicketFiles, type TicketKind } from '../../../../api/ticketImport';

interface Props {
  tickets: TicketFiles;
  onChange: (tickets: TicketFiles) => void;
}

/** Same icons and titles as the Docs tab's sections, so the sorting reads the same. */
const KIND: Record<TicketKind, { icon: LucideIcon; title: string }> = {
  flight: { icon: Plane, title: 'bookings.sections.flights' },
  hotel: { icon: BedDouble, title: 'bookings.sections.hotels' },
  transport: { icon: Train, title: 'bookings.sections.transport' },
  event: { icon: Ticket, title: 'bookings.sections.events' },
};

function KindRow({ kind, files, onChange, onError }: {
  kind: TicketKind;
  files: File[];
  onChange: (files: File[]) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { icon, title } = KIND[kind];
  const full = files.length >= MAX_TICKET_FILES_PER_KIND;

  function add(list: FileList | null) {
    if (!list || list.length === 0) return;
    const { accepted, errors } = withinLimit(Array.from(list), 'media');
    const seen = new Set(files.map(f => `${f.name}:${f.size}`));
    const fresh = accepted.filter(f => !seen.has(`${f.name}:${f.size}`));
    const kept = [...files, ...fresh].slice(0, MAX_TICKET_FILES_PER_KIND);
    onError(errors[0] ?? (kept.length < files.length + fresh.length
      ? t('dashboard.newTripWizard.ticketsMax', { count: MAX_TICKET_FILES_PER_KIND })
      : null));
    onChange(kept);
  }

  return (
    <li className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 p-3.5">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
          <Icon as={icon} size={17} className="text-slate-500 dark:text-slate-400" />
        </span>
        <span className="flex-1 min-w-0 text-[14px] font-semibold text-slate-900 dark:text-slate-100 truncate">{t(title)}</span>
        <button
          type="button" disabled={full} onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 text-xs font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          <Icon as={Plus} size={14} /> {t('common.add')}
        </button>
        <input
          ref={inputRef} type="file" multiple className="hidden" accept={ATTACHMENT_ACCEPT}
          aria-label={t('dashboard.newTripWizard.ticketsAddTo', { kind: t(title) })}
          onChange={e => { add(e.target.files); e.target.value = ''; }}
        />
      </div>
      {files.length > 0 && (
        <ul className="mt-2.5 space-y-1.5 pl-12">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-[13px] text-slate-600 dark:text-slate-300">
              <Icon as={stagedFileIcon(f)} size={14} className="text-slate-400 shrink-0" />
              <span className="flex-1 truncate">{f.name}</span>
              <button
                type="button" onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="text-slate-400 hover:text-red-500 cursor-pointer" aria-label={t('common.remove')}
              >
                <Icon as={X} size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function BookingsStep({ tickets, onChange }: Props) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <ul className="space-y-2.5">
        {TICKET_KINDS.map(kind => (
          <KindRow
            key={kind} kind={kind} files={tickets[kind]} onError={setError}
            onChange={files => onChange({ ...tickets, [kind]: files })}
          />
        ))}
      </ul>
      {error && <p className="text-[13px] text-red-500">{error}</p>}
    </div>
  );
}
